// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Plus,
  Edit3,
  Trash2,
  Loader2,
  ChevronLeft,
  Grid3x3,
  CheckCircle2,
  Eye,
  Users,
  Layers,
} from "lucide-react";
import toast from "react-hot-toast";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import PageLoader from "../../components/PageLoader";
import spaceService from "../../services/spaceService";
import scenarioService from "../../services/scenarioService";
import { formatErrorMessage } from "../../utils/apiHelper";

const SPACE_TYPES = [
  "event_area",
  "parking_spot",
  "delivery_zone",
  "radbox_slot",
  "charging_point",
];

const unwrapList = (response) => {
  if (Array.isArray(response)) return response;
  return (
    response?.items ?? response?.data ?? response?.results ?? response ?? []
  );
};

/**
 * Read-only overview of every vhp_space record. The backend's
 * GET /admin/spaces/ now returns all spaces visible to the current tenant
 * in a single call, with each row including `scenario_name`.
 *
 * Filters: search (free text), scenario_id, space_type. Server-side.
 */
const SpacesList = () => {
  const navigate = useNavigate();
  const [spaces, setSpaces] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const [filterSearch, setFilterSearch] = useState("");
  const [filterScenarioId, setFilterScenarioId] = useState("");
  const [filterSpaceType, setFilterSpaceType] = useState("");

  useEffect(() => {
    let cancelled = false;
    scenarioService
      .getScenarios({ limit: 200 })
      .then((response) => {
        if (cancelled) return;
        setScenarios(unwrapList(response).filter((s) => s?.id));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    spaceService
      .listSpaces({
        scenarioId: filterScenarioId || undefined,
        spaceType: filterSpaceType || undefined,
        search: filterSearch.trim() || undefined,
        limit: 500,
      })
      .then((response) => {
        if (cancelled) return;
        const items = unwrapList(response);
        setSpaces(items);
        setSelected((prev) =>
          prev && items.some((s) => s.id === prev.id) ? prev : null,
        );
      })
      .catch((err) => {
        if (cancelled) return;
        setError(formatErrorMessage(err));
        setSpaces([]);
        setSelected(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filterScenarioId, filterSpaceType, filterSearch, refreshKey]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await spaceService.deleteSpace(deleteTarget.id);
      toast.success("Fläche gelöscht.");
      setDeleteTarget(null);
      if (selected?.id === deleteTarget.id) setSelected(null);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      console.error("Error deleting space:", err);
      toast.error(formatErrorMessage(err) || "Löschen fehlgeschlagen.", {
        duration: 8000,
      });
    } finally {
      setDeleting(false);
    }
  };

  const scenariosById = useMemo(
    () =>
      scenarios.reduce((acc, s) => {
        acc[s.id] = s;
        return acc;
      }, {}),
    [scenarios],
  );

  const scenarioLabelFor = (space) =>
    space.scenario_name ||
    scenariosById[space.scenario_id]?.name ||
    scenariosById[space.scenario_id]?.scenario_code ||
    (space.scenario_id ? `Szenario #${space.scenario_id}` : "—");

  const spacesByScenario = useMemo(() => {
    const map = new Map();
    spaces.forEach((space) => {
      const key = space.scenario_id ?? "__unassigned";
      if (!map.has(key)) {
        const scenario = scenariosById[space.scenario_id];
        map.set(key, {
          key: String(key),
          scenarioId: space.scenario_id,
          scenarioLabel:
            space.scenario_name ||
            scenario?.name ||
            scenario?.scenario_code ||
            (space.scenario_id
              ? `Szenario #${space.scenario_id}`
              : "Ohne Szenario"),
          items: [],
        });
      }
      map.get(key).items.push(space);
    });
    return Array.from(map.values());
  }, [spaces, scenariosById]);

  const hasActiveFilters =
    filterSearch !== "" || filterScenarioId !== "" || filterSpaceType !== "";
  const clearFilters = () => {
    setFilterSearch("");
    setFilterScenarioId("");
    setFilterSpaceType("");
  };

  const stats = useMemo(
    () => ({
      total: spaces.length,
      active: spaces.filter((s) => s.is_active !== false).length,
      mapVisible: spaces.filter((s) => !!s.svg_path).length,
      capacity: spaces.reduce((sum, s) => sum + (Number(s.capacity) || 0), 0),
    }),
    [spaces],
  );

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title="Flächen (Spaces)"
        subtitle="Buchbare Flächen für Szenarien und Map Editor definieren"
        buttons={[
          {
            text: "Fläche anlegen",
            icon: <Plus className="w-4 h-4" />,
            onClick: () => {
              if (filterScenarioId) {
                navigate(`/admin/scenario-editor/${filterScenarioId}`);
              } else {
                navigate(`/admin/scenario-editor`);
              }
            },
            className:
              "bg-primary-color text-white px-4 py-2 rounded-md hover:opacity-90 flex items-center gap-2",
          },
        ]}
      />
      <div className="mx-auto px-4 lg:px-4 py-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatCard
            label="Flächen"
            value={stats.total}
            icon={Grid3x3}
            tone="violet"
          />
          <StatCard
            label="Aktiv"
            value={stats.active}
            icon={CheckCircle2}
            tone="emerald"
          />
          <StatCard
            label="Map-sichtbar"
            value={stats.mapVisible}
            icon={Eye}
            tone="indigo"
          />
          <StatCard
            label="Kapazität"
            value={stats.capacity}
            icon={Users}
            tone="cyan"
          />
        </div>

        <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-4 py-3 mb-4 flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Szenario:
            </label>
            <select
              value={filterScenarioId}
              onChange={(e) => setFilterScenarioId(e.target.value)}
              className="px-3 py-1.5 text-sm bg-gray-50 dark:bg-slate-700 border-0 rounded-lg min-w-[180px] focus:ring-2 focus:ring-primary-color/30 focus:outline-none"
            >
              <option value="">Alle Szenarien</option>
              {scenarios.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.scenario_code || `#${s.id}`}
                </option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 dark:text-slate-300">
              Typ:
            </label>
            <select
              value={filterSpaceType}
              onChange={(e) => setFilterSpaceType(e.target.value)}
              className="px-3 py-1.5 text-sm bg-gray-50 dark:bg-slate-700 border-0 rounded-lg min-w-[160px] focus:ring-2 focus:ring-primary-color/30 focus:outline-none"
            >
              <option value="">Alle Typen</option>
              {SPACE_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-auto text-xs text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-slate-100 underline"
            >
              Zurücksetzen
            </button>
          )}
        </div>

        {loading ? (
          <PageLoader message="Lade Flächen..." />
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm rounded-lg px-4 py-3">
            {error}
          </div>
        ) : spaces.length === 0 ? (
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-8 text-center text-gray-500 dark:text-slate-400">
            Keine Flächen vorhanden.
          </div>
        ) : selected ? (
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="inline-flex items-center gap-1.5 text-sm text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-slate-100"
              >
                <ChevronLeft className="w-4 h-4" />
                Zurück zur Liste
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (selected.scenario_id) {
                      navigate(
                        `/admin/scenario-editor/${selected.scenario_id}`,
                      );
                    } else {
                      navigate("/admin/scenario-editor");
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-slate-600 text-gray-700 dark:text-slate-200 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700"
                >
                  <Edit3 className="w-4 h-4" />
                  Im Editor öffnen
                </button>
                <button
                  type="button"
                  onClick={() => setDeleteTarget(selected)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <Trash2 className="w-4 h-4" />
                  Löschen
                </button>
              </div>
            </div>
            <SpaceDetail
              space={selected}
              siblings={
                spacesByScenario.find(
                  (g) => g.scenarioId === selected.scenario_id,
                )?.items || [selected]
              }
              onSelectSpace={setSelected}
            />
          </div>
        ) : (
          <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-slate-400 border-b border-gray-200 dark:border-slate-700">
                    <th className="px-4 py-3 font-semibold">Fläche</th>
                    <th className="px-4 py-3 font-semibold">Code</th>
                    <th className="px-4 py-3 font-semibold">Szenario</th>
                    <th className="px-4 py-3 font-semibold">Kapazität</th>
                    <th className="px-4 py-3 font-semibold">Stundensatz</th>
                    <th className="px-4 py-3 font-semibold">Use Cases</th>
                    <th className="px-4 py-3 font-semibold">Map</th>
                    <th className="px-4 py-3 font-semibold">Status</th>
                    <th className="px-4 py-3 font-semibold text-right">
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-slate-700">
                  {spaces.map((space) => (
                    <tr
                      key={space.id}
                      onClick={() => setSelected(space)}
                      className="group cursor-pointer hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-slate-100">
                          {space.name || "(ohne Name)"}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-gray-500 dark:text-slate-400">
                        {space.space_code || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-slate-300">
                        {scenarioLabelFor(space)}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-slate-300">
                        {space.capacity ?? "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-slate-300">
                        {space.hourly_rate != null
                          ? `€${Number(space.hourly_rate).toFixed(2)}`
                          : "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-slate-400">
                        {Array.isArray(space.use_cases) &&
                        space.use_cases.length ? (
                          space.use_cases.join(", ")
                        ) : (
                          <span className="text-gray-400 dark:text-slate-500">
                            —
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge active={!!space.map_visible} />
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge active={space.is_active !== false} />
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (space.scenario_id) {
                                navigate(
                                  `/admin/scenario-editor/${space.scenario_id}`,
                                );
                              } else {
                                navigate("/admin/scenario-editor");
                              }
                            }}
                            className="p-1.5 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400"
                            title="Im Editor öffnen"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(space);
                            }}
                            className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-red-500 dark:text-red-400"
                            title="Löschen"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!selected && !loading && !error && (
          <div className="mt-4 flex items-start gap-3 rounded-xl border border-violet-200 dark:border-violet-900/50 bg-violet-50 dark:bg-violet-900/20 px-4 py-3">
            <Layers className="w-5 h-5 text-violet-600 dark:text-violet-300 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-violet-700 dark:text-violet-300">
                Map Editor Integration
              </p>
              <p className="text-xs text-violet-700/80 dark:text-violet-300/80 mt-0.5">
                Flächen mit „Map-sichtbar“ werden im SVG Map Editor angezeigt
                und können dort bearbeitet werden. Deaktivierte Flächen sind
                weiterhin konfigurierbar, aber nicht buchbar.
              </p>
            </div>
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => !deleting && setDeleteTarget(null)}
            aria-hidden
          />
          <div className="relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100 mb-2">
              Fläche löschen?
            </h3>
            <p className="text-sm text-gray-600 dark:text-slate-300 mb-4">
              &laquo;
              <strong>
                {deleteTarget.name ||
                  deleteTarget.space_code ||
                  deleteTarget.id}
              </strong>
              &raquo; wird unwiderruflich gelöscht.
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="px-4 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="flex items-center gap-2 px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:opacity-90 disabled:opacity-50"
              >
                {deleting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Small KPI card: uppercase label on the left, big number underneath, soft
 * tinted icon chip on the right. Tones map to a fill+text color pair.
 */
const StatCard = ({ label, value, icon: Icon, tone = "indigo" }) => {
  const tones = {
    indigo:
      "bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-300",
    violet:
      "bg-violet-100 dark:bg-violet-900/30 text-violet-600 dark:text-violet-300",
    emerald:
      "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-300",
    cyan: "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-600 dark:text-cyan-300",
  };
  return (
    <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 flex items-center justify-between gap-3">
      <div className="min-w-0">
        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">
          {label}
        </p>
        <p className="text-2xl font-bold text-gray-900 dark:text-slate-100 mt-1">
          {value}
        </p>
      </div>
      {Icon && (
        <div
          className={`p-2.5 rounded-xl shrink-0 ${tones[tone] || tones.indigo}`}
        >
          <Icon className="w-5 h-5" />
        </div>
      )}
    </div>
  );
};

/**
 * Pill badge for the Map / Status columns: green dot + "Aktiv" when active,
 * grey dot + "Inaktiv" otherwise.
 */
const StatusBadge = ({ active }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      active
        ? "bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300"
        : "bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400"
    }`}
  >
    <span
      className={`w-1.5 h-1.5 rounded-full ${
        active ? "bg-emerald-500" : "bg-gray-400"
      }`}
    />
    {active ? "Aktiv" : "Inaktiv"}
  </span>
);

const KeyValue = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500">
      {label}
    </div>
    <div className="text-sm text-gray-900 dark:text-slate-100 break-words">
      {value === null || value === undefined || value === "" ? "—" : value}
    </div>
  </div>
);

const ColorValue = ({ label, value }) => (
  <div>
    <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500">
      {label}
    </div>
    <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-slate-100">
      {value ? (
        <>
          <span
            className="inline-block w-5 h-5 rounded border border-gray-300 dark:border-slate-600 shrink-0"
            style={{ backgroundColor: value }}
          />
          {value}
        </>
      ) : (
        "—"
      )}
    </div>
  </div>
);

const SpaceDetail = ({ space, siblings = [], onSelectSpace }) => {
  const [scenarioSvg, setScenarioSvg] = useState(null);
  const [scenarioSvgLoading, setScenarioSvgLoading] = useState(false);

  useEffect(() => {
    if (!space?.scenario_id) {
      setScenarioSvg(null);
      return undefined;
    }
    let cancelled = false;
    setScenarioSvgLoading(true);
    scenarioService
      .getScenarioById(space.scenario_id)
      .then((response) => {
        if (cancelled) return;
        const data = response?.data ?? response;
        setScenarioSvg(data?.svg_content ?? null);
      })
      .catch(() => {
        if (!cancelled) setScenarioSvg(null);
      })
      .finally(() => {
        if (!cancelled) setScenarioSvgLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [space?.scenario_id]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 truncate">
            {space.name || space.space_code || "(ohne Name)"}
          </h2>
          <p className="text-xs text-gray-500 dark:text-slate-400 mt-0.5 truncate">
            ID: {space.id}
          </p>
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1">
          SVG Vorschau
        </div>
        {space.svg_path || scenarioSvg ? (
          <SvgPreview
            activeId={space.id}
            siblings={siblings.length > 0 ? siblings : [space]}
            onSelectSpace={onSelectSpace}
            scenarioSvg={scenarioSvg}
            loading={scenarioSvgLoading}
          />
        ) : (
          <div className="bg-gray-50 dark:bg-slate-700/50 border border-dashed border-gray-300 dark:border-slate-600 rounded-lg p-6 text-center text-sm text-gray-500 dark:text-slate-400">
            Kein SVG-Pfad gespeichert
          </div>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <KeyValue label="Code" value={space.space_code} />
        <KeyValue label="Nummer" value={space.space_number} />
        <KeyValue label="Typ" value={space.space_type} />
        <KeyValue label="Kapazität" value={space.capacity} />
        <KeyValue label="Einheit" value={space.capacity_unit} />
        <KeyValue label="Wartung" value={space.maintenance_status} />
        <KeyValue label="Länge (m)" value={space.length_m} />
        <KeyValue label="Breite (m)" value={space.width_m} />
        <KeyValue label="Höhe (m)" value={space.height_m} />
        <KeyValue label="Fläche (m²)" value={space.area_sqm} />
        <KeyValue label="Ausrichtung (°)" value={space.orientation} />
        <KeyValue label="GPS Lat" value={space.gps_lat} />
        <KeyValue label="GPS Lng" value={space.gps_lng} />
        <KeyValue label="Aktiv" value={space.is_active ? "ja" : "nein"} />
        <KeyValue label="Buchbar" value={space.is_bookable ? "ja" : "nein"} />
        <ColorValue label="Fill Color" value={space.equipment?._style?.fill} />
        <ColorValue
          label="Stroke Color"
          value={space.equipment?._style?.stroke}
        />
        <KeyValue
          label="Stroke Width"
          value={space.equipment?._style?.strokeWidth}
        />
        <KeyValue
          label="Rotation (°)"
          value={space.equipment?._style?.rotation}
        />
        <KeyValue label="Auf Karte" value={space.map_visible ? "ja" : "nein"} />
        <KeyValue
          label="Stundensatz (€)"
          value={
            space.hourly_rate != null
              ? Number(space.hourly_rate).toFixed(2)
              : null
          }
        />
        <KeyValue
          label="Nutzungsarten"
          value={
            Array.isArray(space.use_cases) && space.use_cases.length
              ? space.use_cases.join(", ")
              : null
          }
        />
      </div>

      {space.description && (
        <div>
          <div className="text-xs uppercase tracking-wide text-gray-400 dark:text-slate-500 mb-1">
            Beschreibung
          </div>
          <p className="text-sm text-gray-700 dark:text-slate-300 whitespace-pre-wrap">
            {space.description}
          </p>
        </div>
      )}
    </div>
  );
};

const pathBbox = (path) => {
  if (!path) return null;
  const nums = (path.match(/-?\d*\.?\d+/g) || []).map(Number);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i + 1 < nums.length; i += 2) {
    const x = nums[i];
    const y = nums[i + 1];
    if (Number.isFinite(x) && Number.isFinite(y)) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }
  if (!Number.isFinite(minX) || !Number.isFinite(maxX)) return null;
  return { minX, minY, maxX, maxY };
};

const parseScenarioSvg = (svgString) => {
  if (!svgString || typeof svgString !== "string") return null;
  const match = svgString.match(/<svg([^>]*)>([\s\S]*)<\/svg>/i);
  if (!match) return null;
  const attrs = match[1];
  const inner = match[2];
  const viewBoxMatch = attrs.match(/viewBox\s*=\s*["']([^"']+)["']/i);
  const widthMatch = attrs.match(/\swidth\s*=\s*["']([^"']+)["']/i);
  const heightMatch = attrs.match(/\sheight\s*=\s*["']([^"']+)["']/i);
  let viewBox = viewBoxMatch ? viewBoxMatch[1] : null;
  if (!viewBox && widthMatch && heightMatch) {
    viewBox = `0 0 ${widthMatch[1]} ${heightMatch[1]}`;
  }
  return { viewBox, inner };
};

/**
 * Interactive preview.
 *
 * - Backdrop: parent scenario's saved `svg_content` (rendered dimmed).
 * - Foreground: every sibling space in the same scenario, each as a
 *   clickable polygon with a small label. The active space is highlighted
 *   in amber/red; the rest are muted blue.
 */
const SvgPreview = ({
  activeId,
  siblings,
  onSelectSpace,
  scenarioSvg,
  loading,
}) => {
  const scenario = parseScenarioSvg(scenarioSvg);
  const activeSpace = siblings.find((s) => s.id === activeId);

  let viewBox;
  if (scenario?.viewBox) {
    viewBox = scenario.viewBox;
  } else {
    let union = null;
    siblings.forEach((s) => {
      const bbox = pathBbox(s.svg_path);
      if (!bbox) return;
      if (!union) {
        union = { ...bbox };
      } else {
        union.minX = Math.min(union.minX, bbox.minX);
        union.minY = Math.min(union.minY, bbox.minY);
        union.maxX = Math.max(union.maxX, bbox.maxX);
        union.maxY = Math.max(union.maxY, bbox.maxY);
      }
    });
    if (union) {
      const pad = 20;
      viewBox = `${union.minX - pad} ${union.minY - pad} ${union.maxX - union.minX + 2 * pad} ${union.maxY - union.minY + 2 * pad}`;
    } else {
      viewBox = "0 0 800 600";
    }
  }

  return (
    <div className="bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
      <div className="relative">
        <svg
          viewBox={viewBox}
          preserveAspectRatio="xMidYMid meet"
          className="w-full h-96"
        >
          {scenario && (
            <g
              opacity="0.45"
              dangerouslySetInnerHTML={{ __html: scenario.inner }}
            />
          )}
          {siblings.map((s) => {
            if (!s.svg_path) return null;
            const isActive = s.id === activeId;
            const bbox = pathBbox(s.svg_path);
            const label = s.name || s.space_code || "";
            const style = s.equipment?._style || {};
            const rot = style.rotation ?? s.orientation ?? 0;
            const cx = bbox ? (bbox.minX + bbox.maxX) / 2 : 0;
            const cy = bbox ? (bbox.minY + bbox.maxY) / 2 : 0;
            const rotateTransform = rot ? `rotate(${rot} ${cx} ${cy})` : "";
            return (
              <g
                key={s.id}
                onClick={() => onSelectSpace && onSelectSpace(s)}
                style={{ cursor: "pointer" }}
                transform={rotateTransform}
              >
                <path
                  d={s.svg_path}
                  fill={style.fill || (isActive ? "#fde68a" : "#bfdbfe")}
                  fillOpacity={isActive ? 0.85 : 0.5}
                  stroke={isActive ? "#dc2626" : style.stroke || "#1d4ed8"}
                  strokeWidth={isActive ? 2.5 : style.strokeWidth || 1.5}
                  vectorEffect="non-scaling-stroke"
                />
                {isActive && (
                  <path
                    d={s.svg_path}
                    fill="none"
                    stroke="#dc2626"
                    strokeWidth={3}
                    strokeDasharray="6 3"
                    vectorEffect="non-scaling-stroke"
                    pointerEvents="none"
                  />
                )}
                {bbox && label && (
                  <text
                    x={cx}
                    y={cy}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize={style.fontSize || 12}
                    fontWeight={isActive ? 700 : 500}
                    fill={isActive ? "#991b1b" : "#1e3a8a"}
                    pointerEvents="none"
                    style={{ paintOrder: "stroke" }}
                    stroke="#ffffff"
                    strokeWidth="3"
                    strokeOpacity="0.7"
                  >
                    {label}
                  </text>
                )}
              </g>
            );
          })}
        </svg>
        {loading && (
          <div className="absolute top-2 right-2 text-xs bg-white/80 dark:bg-slate-800/80 text-gray-600 dark:text-slate-300 px-2 py-0.5 rounded">
            Lade Plan…
          </div>
        )}
        {!scenario && !loading && (
          <div className="absolute top-2 right-2 text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2 py-0.5 rounded">
            Kein Szenario-Plan
          </div>
        )}
      </div>
      {activeSpace?.svg_path && (
        <div className="px-3 py-1.5 text-xs font-mono text-gray-500 dark:text-slate-400 border-t border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 truncate">
          {activeSpace.svg_path}
        </div>
      )}
    </div>
  );
};

export default SpacesList;
